import ApplicationController from './application_controller'

// Thumbnail preview + per-file removal for incident photo uploads, managing
// input.files via DataTransfer so removed files are excluded before submit.
export default class extends ApplicationController {
  static targets = ['input', 'preview', 'picker', 'spinner']
  static values = { removeLabel: String, autoSubmit: Boolean }

  connect () {
    // Detail upload submits over the network; restore the picker if it fails
    // (on success the turbo frame is replaced and this controller is gone).
    if (!this.autoSubmitValue) return

    this.form = this.element.closest('form')
    this.onSubmitEnd = () => this.#stopUploading()
    this.form?.addEventListener('turbo:submit-end', this.onSubmitEnd)
  }

  disconnect () {
    this.form?.removeEventListener('turbo:submit-end', this.onSubmitEnd)
  }

  preview () {
    // Detail upload: a chosen file is added immediately (no extra button).
    if (this.autoSubmitValue) {
      // Show progress before submitting so users wait instead of re-picking
      // the same photos while the upload is in flight.
      this.#startUploading()
      this.form?.requestSubmit()
      return
    }

    this.#render(Array.from(this.inputTarget.files))
  }

  #startUploading () {
    // Hide the (clickable) picker without disabling the input, since a disabled
    // file field is not included in the form submission.
    if (this.hasPickerTarget) this.pickerTarget.classList.add('hidden')
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.replace('hidden', 'flex')
  }

  #stopUploading () {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.replace('flex', 'hidden')
    if (this.hasPickerTarget) this.pickerTarget.classList.remove('hidden')
  }

  remove (event) {
    event.preventDefault()
    const index = Number(event.currentTarget.dataset.index)
    const files = Array.from(this.inputTarget.files)
    files.splice(index, 1)

    const dataTransfer = new DataTransfer()
    files.forEach((file) => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files

    this.#render(files)
  }

  #render (files) {
    this.previewTarget.replaceChildren()

    files.forEach((file, index) => {
      if (!file.type.startsWith('image/')) return

      const row = document.createElement('div')
      row.className = 'flex items-center gap-3'

      const image = document.createElement('img')
      image.src = URL.createObjectURL(file)
      image.className = 'h-32 w-32 rounded object-cover border'
      image.onload = () => URL.revokeObjectURL(image.src)

      const meta = document.createElement('div')

      const name = document.createElement('p')
      name.textContent = file.name
      name.className = 'text-sm text-gray-700'

      const button = document.createElement('button')
      button.type = 'button'
      button.textContent = this.removeLabelValue || 'Delete'
      button.className = 'mt-1 rounded bg-red-600 px-3 py-1 text-sm font-medium text-white hover:bg-red-500'
      button.dataset.index = index
      button.dataset.action = 'file-thumbnails#remove'

      meta.append(name, button)
      row.append(image, meta)
      this.previewTarget.append(row)
    })
  }
}
